import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { HelpCircle, Check, X, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Hook: lädt Lese-Checks eines Moduls und liefert Antwort-Funktion
export const useReadingChecks = (moduleId, authHeader) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!moduleId) return undefined;
    axios.get(`${API}/api/learning/reading-checks/${moduleId}`, authHeader)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData({ total: 0, questions: [], all_done: true }); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleId]);

  const markDone = useCallback((questionId) => {
    setData((d) => {
      if (!d) return d;
      const questions = d.questions.map((q) => (q.id === questionId ? { ...q, answered_correct: true } : q));
      return { ...d, questions, done: questions.filter((q) => q.answered_correct).length, all_done: questions.every((q) => q.answered_correct) };
    });
  }, []);

  return {
    total: data?.total || 0,
    done: data?.questions?.filter((q) => q.answered_correct).length || 0,
    allDone: data ? data.all_done !== false : true,
    questions: data?.questions || [],
    markDone,
  };
};

// Eine Verständnisfrage als Karte im Lesefluss
export const ReadingCheckCard = ({ moduleId, check, index, total, authHeader, onCorrect }) => {
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null); // {correct, explanation}
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (selected === null || busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/learning/reading-checks/${moduleId}/answer`,
        { question_id: check.id, selected_index: selected }, authHeader);
      setResult(r.data);
      if (r.data.correct) onCorrect?.(check.id);
    } catch { setResult(null); }
    setBusy(false);
  };

  const solved = check.answered_correct || result?.correct;

  return (
    <div
      className="my-8 rounded-2xl border p-6"
      style={{ backgroundColor: solved ? '#EEF3EA' : '#F7F1E6', borderColor: solved ? '#9DB58A' : '#D89232' }}
      data-testid={`reading-check-${index + 1}`}
    >
      <div className="flex items-center gap-2 mb-3">
        {solved
          ? <Check className="w-4 h-4" style={{ color: '#4E6B3E' }} />
          : <HelpCircle className="w-4 h-4" style={{ color: '#8F650F' }} />}
        <p className="text-[10.5px] uppercase font-bold" style={{ letterSpacing: '0.24em', color: solved ? '#4E6B3E' : '#8F650F' }}>
          Lese-Check {index + 1} von {total} {solved && '· beantwortet'}
        </p>
      </div>
      <p className="text-[15px] font-semibold text-espresso leading-relaxed mb-4">{check.question}</p>
      {!solved && (
        <>
          <div className="space-y-2 mb-4">
            {check.options.map((opt, oi) => (
              <label
                key={oi}
                className={`flex items-start gap-3 px-4 py-2.5 rounded-xl border cursor-pointer transition-colors ${selected === oi ? 'border-terra bg-white' : 'border-cream-300 bg-white/60 hover:border-sand-400'}`}
              >
                <input type="radio" name={`rc-${check.id}`} checked={selected === oi}
                  onChange={() => { setSelected(oi); setResult(null); }}
                  className="mt-0.5 accent-[#E07A5F]" data-testid={`reading-check-${index + 1}-opt-${oi}`} />
                <span className="text-[13.5px] text-sand-800 leading-snug">{opt}</span>
              </label>
            ))}
          </div>
          {result && !result.correct && (
            <p className="flex items-center gap-1.5 text-[13px] font-semibold mb-3" style={{ color: '#A83232' }} data-testid={`reading-check-${index + 1}-wrong`}>
              <X className="w-4 h-4" /> Leider nicht richtig — lies die Stelle noch einmal und versuch es erneut.
            </p>
          )}
          <button
            onClick={submit}
            disabled={selected === null || busy}
            className="px-6 py-2.5 rounded-full text-[11px] font-bold uppercase tracking-widest disabled:opacity-40"
            style={{ border: '1.5px solid #E07A5F', color: '#E07A5F' }}
            data-testid={`reading-check-${index + 1}-submit`}
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : 'Antwort prüfen'}
          </button>
        </>
      )}
      {solved && (result?.explanation || null) && (
        <p className="text-[13px] leading-relaxed" style={{ color: '#4E6B3E' }}>{result.explanation}</p>
      )}
    </div>
  );
};
